import { useCallback, useEffect, useRef } from "react";
import { Account, UserAccount } from "../data/types";
import SignalRServiceManager from "./SignalRServiceManager";

type AccountSignalRProps = {
    onUpsertAccount?: (account: Account) => void;
    onStorageSizeChange?: (accountId: string, storageSize: number) => void;
    primaryAccount?: UserAccount;
}
export const useAccountSignalR = (props: AccountSignalRProps) => {

    const { onUpsertAccount, primaryAccount } = props;
    const messageName = "upsertAccount";

    // Use useRef to maintain stable reference
    const onUpsertAccountRef = useRef(onUpsertAccount);
    const onStorageSizeChangeRef = useRef(props.onStorageSizeChange);

    useEffect(() => {
        onUpsertAccountRef.current = onUpsertAccount;
        onStorageSizeChangeRef.current = props.onStorageSizeChange;
    }, [onUpsertAccount, props.onStorageSizeChange]);

    const storageSizeChangeHandler = useCallback((acctStorageSizeChnge: string) => {
        const acctStorageSizeInfo = JSON.parse(acctStorageSizeChnge);
        const { accountId, storageSize } = acctStorageSizeInfo;
        onStorageSizeChangeRef.current?.(accountId, storageSize);
    }, []); // Now stable
    const upsertHandler = useCallback((acctJson: string) => {
        try {
            const acct = JSON.parse(acctJson);
            onUpsertAccountRef.current?.(acct);
        } catch (error) {
            console.error("Failed to parse signalR message:", error, acctJson);
        }
    }, []); // Now stable

    useEffect(() => {
        let signalRManager: Awaited<ReturnType<typeof SignalRServiceManager.getInstance>> | null = null;

        const setupSignalR = async () => {
            if (primaryAccount) {
                signalRManager = await SignalRServiceManager.getInstance(primaryAccount);
                signalRManager?.on(messageName, upsertHandler);
                signalRManager?.on("updateStorageSize", storageSizeChangeHandler);
            }
        };

        setupSignalR();

        return () => {
            signalRManager?.off(messageName, upsertHandler);
            signalRManager?.off('updateStorageSize', storageSizeChangeHandler);
        };
    }, [primaryAccount, upsertHandler]);

    return null;
}